export interface ColumnSelectionState {
  selectedColumnIds: string[]
  anchorId: string | null
}

export interface ColumnSelectionModifiers {
  shiftKey: boolean
  toggleKey: boolean
}

/**
 * Pure reducer for whole-column header selection.
 *
 * - `toggleKey` (Ctrl/Cmd+click): add the column if unselected, remove it if selected, leaving
 *   the rest of the selection untouched. The clicked column becomes the new anchor.
 * - `shiftKey` (Shift+click) with an anchor: select the contiguous range of columns between the
 *   anchor and the clicked column (inclusive), preserving the original anchor for re-extension.
 * - otherwise (plain click, or Shift+click with no anchor): select just the clicked column and
 *   make it the anchor.
 *
 * `orderedColumnIds` must be the columns in display order so ranges span the visible layout.
 */
export const computeColumnSelection = (
  orderedColumnIds: string[],
  current: ColumnSelectionState,
  columnId: string,
  modifiers: ColumnSelectionModifiers
): ColumnSelectionState => {
  if (modifiers.toggleKey) {
    const selectedColumnIds = current.selectedColumnIds.includes(columnId)
      ? current.selectedColumnIds.filter((id) => id !== columnId)
      : [...current.selectedColumnIds, columnId]
    return { selectedColumnIds, anchorId: columnId }
  }

  if (modifiers.shiftKey && current.anchorId) {
    const anchorIndex = orderedColumnIds.indexOf(current.anchorId)
    const targetIndex = orderedColumnIds.indexOf(columnId)
    if (anchorIndex !== -1 && targetIndex !== -1) {
      const [start, end] = anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex]
      return { selectedColumnIds: orderedColumnIds.slice(start, end + 1), anchorId: current.anchorId }
    }
  }

  return { selectedColumnIds: [columnId], anchorId: columnId }
}
